/**
 * universal-lightbox-slide-transition.js
 *
 * Purpose: Re-creates the arrow/keyboard "real sliding" transition effect
 * (originally built for product-template-1.liquid's PhotoSwipe v4 gallery)
 * for ANY PhotoSwipe-family lightbox on the site — including third-party
 * app widgets (e.g. gallery/lookbook apps) that fork PhotoSwipe's BEM
 * class names under a different prefix (e.g. "pswp_lf__..." instead of
 * "pswp__...").
 *
 * WHY GENERIC MATCHING INSTEAD OF HARDCODED CLASSES:
 * Different lightboxes on the same store can use different class
 * prefixes while keeping PhotoSwipe's BEM structure:
 *   root:      .pswp            / .pswp_lf            (+ "--open" modifier)
 *   container: .pswp__container / .pswp_lf__container
 *   arrows:    .pswp__button--arrow--left/right
 *              .pswp_lf__button--arrow--prev/next
 * Rather than listing every known app's classes, this script detects the
 * prefix at click-time from the DOM itself, so it keeps working even if
 * a new gallery app (with yet another prefix) is added later.
 *
 * NOT INCLUDED: any thumbnail-rail building/syncing. This script ONLY
 * adds the slide-move animation.
 *
 * WHERE TO LOAD THIS:
 * Load it ONCE, site-wide — e.g. in theme.liquid right before </body> —
 * so it applies to every lightbox on every page, not just one section/page:
 *
 *   <script src="{{ 'universal-lightbox-slide-transition.js' | asset_url }}" defer></script>
 *
 * If you previously added a page-specific version of this effect
 * (e.g. gallery-slide-transition.js in a single section), remove that
 * one to avoid two competing transitions stacking on the same element.
 */
(function () {
  var SLIDE_MS = 450;
  var prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return;

  // Tracks the "clear" function of the most recent transition, so a
  // stale timeout/listener from an earlier click can never fire
  // mid-way through a later click's animation and wipe it early.
  var pendingClear = null;

  function applyTransition(container) {
    if (!container) return;
    if (pendingClear) pendingClear();

    container.style.transition =
      'transform ' + SLIDE_MS + 'ms cubic-bezier(0.4, 0, 0.22, 1)';
    void container.offsetHeight; // force reflow so the browser has a "before" state to animate from

    var cleared = false;
    var timeoutId;
    function clear() {
      if (cleared) return;
      cleared = true;
      container.style.transition = '';
      container.removeEventListener('transitionend', clear);
      clearTimeout(timeoutId);
      if (pendingClear === clear) pendingClear = null;
    }
    container.addEventListener('transitionend', clear);
    timeoutId = setTimeout(clear, SLIDE_MS + 100); // fallback in case transitionend never fires
    pendingClear = clear;
  }

  // A BEM "block" class for a PhotoSwipe-family root looks like "pswp"
  // or "pswp_lf" — starts with "pswp", has no "__" (that would make it
  // an element, not the block itself). Modifiers like "pswp--open" or
  // "pswp_lf--open" still match once we strip anything from "--" onward.
  function basePrefix(token) {
    var dashIdx = token.indexOf('--');
    return dashIdx === -1 ? token : token.substring(0, dashIdx);
  }

  function isPswpRootToken(token) {
    return token.indexOf('pswp') === 0 && token.indexOf('__') === -1;
  }

  // Walk the event's full composed path (works even if the widget
  // renders inside a Shadow DOM, which some apps use to isolate CSS)
  // and find the nearest PhotoSwipe-family root, returning its prefix
  // ("pswp", "pswp_lf", etc.) and the root element itself.
  function findRootInPath(path) {
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (node.nodeType !== 1 || !node.classList) continue;
      for (var j = 0; j < node.classList.length; j++) {
        var token = node.classList[j];
        if (isPswpRootToken(token)) {
          return { prefix: basePrefix(token), root: node };
        }
      }
    }
    return null;
  }

  // Confirm the actual click/tap landed on that prefix's arrow button
  // (not some unrelated element inside the same root), e.g.
  // "pswp__button--arrow--left" or "pswp_lf__button--arrow--next".
  function pathHasArrowButton(path, prefix) {
    var needle = prefix + '__button--arrow';
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (node.nodeType !== 1 || !node.classList) continue;
      for (var j = 0; j < node.classList.length; j++) {
        if (node.classList[j].indexOf(needle) === 0) return true;
      }
    }
    return false;
  }

  function onArrowPress(e) {
    var path = e.composedPath ? e.composedPath() : [e.target];
    var found = findRootInPath(path);
    if (!found) return;
    if (!pathHasArrowButton(path, found.prefix)) return;

    var container = found.root.querySelector('.' + found.prefix + '__container');
    applyTransition(container);
  }

  // pointerdown/mousedown/touchstart fire before a lightbox library's own
  // click/tap-gesture navigation, so the transition is always armed
  // before the transform actually changes. Capture phase + document-level
  // listeners mean this works no matter which script/app opened the
  // lightbox markup, and no matter where on the page it appears.
  document.addEventListener('pointerdown', onArrowPress, true);
  document.addEventListener('mousedown', onArrowPress, true);
  document.addEventListener('touchstart', onArrowPress, true);

  // Keyboard navigation: find whichever PhotoSwipe-family lightbox is
  // currently open (root class token ending in "--open") and animate
  // its container the same way.
  function findOpenRoot() {
    var candidates = document.querySelectorAll('[class*="pswp"]');
    for (var i = 0; i < candidates.length; i++) {
      var node = candidates[i];
      if (!node.classList) continue;
      var rootToken = null;
      var hasOpenModifier = false;
      for (var j = 0; j < node.classList.length; j++) {
        var token = node.classList[j];
        if (isPswpRootToken(token)) rootToken = token;
        if (/--open$/.test(token)) hasOpenModifier = true;
      }
      if (rootToken && hasOpenModifier) {
        return { prefix: basePrefix(rootToken), root: node };
      }
    }
    return null;
  }

  document.addEventListener(
    'keydown',
    function (e) {
      if (
        e.keyCode === 37 || e.key === 'ArrowLeft' ||
        e.keyCode === 39 || e.key === 'ArrowRight'
      ) {
        var found = findOpenRoot();
        if (!found) return;
        var container = found.root.querySelector('.' + found.prefix + '__container');
        applyTransition(container);
      }
    },
    true
  );
})();